/**
 * Select with Grouped Options Renderer
 * Features: grouped options, custom item rendering (status, icon, avatar, etc.)
 * Supports: status, iconText, textWithDescription, textWithAvatar, textWithAvatarDescription
 */
import React from 'react';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    SelectGroup,
    SelectLabel,
    SelectSeparator,
} from '@/components/ui/select';
import * as LucideIcons from 'lucide-react';
import type { FieldRenderer, OptionGroup } from '@/MyCode/core';

/**
 * Status Dot Component - used by selectWithOptions for status type
 */
const StatusDot = ({ className }: { className?: string }) => (
    <span className={`inline-block size-2 rounded-full ${className}`} aria-hidden="true" />
);

export const renderSelectWithOptions: FieldRenderer = ({
    field,
    value,
    hasError,
    handleChange,
    isRequired
}) => {
    const groups = (field as any).groupedOptions?.groups || [];
    const customDivType = (field as any).customDiv || 'default';

    // Helper function to render custom item content based on type
    const renderCustomItem = (option: any) => {
        switch (customDivType) {
            case 'status':
                return (
                    <span className="flex items-center gap-2">
                        <StatusDot className={option.statusColor || 'bg-gray-500'} />
                        <span className="truncate">{option.label}</span>
                    </span>
                );

            case 'iconText':
                const IconComponent = option.icon ? LucideIcons[option.icon as keyof typeof LucideIcons] : null;
                return (
                    <span className="flex items-center gap-2">
                        {IconComponent && <IconComponent size={16} aria-hidden="true" />}
                        <span className="truncate">{option.label}</span>
                    </span>
                );

            case 'textWithDescription':
                return (
                    <span className="block">
                        <span className="block">{option.label}</span>
                        {option.description && (
                            <span className="mt-1 block text-xs text-muted-foreground" data-desc>
                                {option.description}
                            </span>
                        )}
                    </span>
                );

            case 'textWithAvatar':
                return (
                    <span className="flex items-center gap-2">
                        {option.avatar && (
                            <img
                                className="size-5 rounded"
                                src={option.avatar}
                                alt={option.label}
                                width={20}
                                height={20}
                            />
                        )}
                        <span className="truncate">{option.label}</span>
                    </span>
                );

            case 'textWithAvatarDescription':
                return (
                    <span className="flex items-center gap-2">
                        {option.avatar && (
                            <img
                                className="rounded-full"
                                src={option.avatar}
                                alt={option.label}
                                width={40}
                                height={40}
                            />
                        )}
                        <span>
                            <span className="block font-medium">{option.label}</span>
                            {option.subtitle && (
                                <span className="mt-0.5 block text-xs text-muted-foreground">
                                    {option.subtitle}
                                </span>
                            )}
                        </span>
                    </span>
                );

            default:
                return <span className="truncate">{option.label}</span>;
        }
    };

    return (
        <div className="space-y-2">
            <Label htmlFor={field.name}>
                {field.label}
                {isRequired && <span className="text-destructive ml-1">*</span>}
            </Label>
            {field.description && (
                <p className="text-sm text-muted-foreground">{field.description}</p>
            )}
            <div className="w-full">
                <Select
                    value={value || ''}
                    onValueChange={(val) => handleChange(field.name, val)}
                >
                    <SelectTrigger id={field.name} className={hasError ? 'w-full border-destructive' : 'w-full'}>
                        <SelectValue placeholder={field.placeholder || 'Select an option'} />
                    </SelectTrigger>
                    <SelectContent>
                        {groups.map((group: OptionGroup, groupIndex: number) => (
                            <React.Fragment key={groupIndex}>
                                {groupIndex > 0 && <SelectSeparator />}
                                <SelectGroup>
                                    <SelectLabel>{group.label}</SelectLabel>
                                    {group.items.map((option) => (
                                        <SelectItem key={option.value} value={option.value}>
                                            {renderCustomItem(option)}
                                        </SelectItem>
                                    ))}
                                </SelectGroup>
                            </React.Fragment>
                        ))}
                    </SelectContent>
                </Select>
            </div>
        </div>
    );
};

export default renderSelectWithOptions;